<template>
  <div class="pdf-main-container">
    <v-btn fab small color="white" elevation="2" class="pdf-fab pdf-fab--back" @click="$emit('back')">
      <v-icon>{{ backIcon }}</v-icon>
    </v-btn>

    <v-chip v-if="totalPages > 0" small color="white" elevation="2" class="pdf-fab pdf-fab--page-count">
      {{ totalPages }} صفحات
    </v-chip>

    <div class="pdf-scroll-view">
      <div v-if="loading" class="status-msg">جاري تحميل الفاتورة...</div>

      <div v-for="pageNo in totalPages" :key="pageNo" class="page-wrapper">
        <canvas :ref="'canvas_' + pageNo"></canvas>
      </div>
    </div>

    <v-btn fab color="primary" elevation="4" class="pdf-fab pdf-fab--print" @click="printPDF">
      <v-icon>mdi-printer</v-icon>
    </v-btn>

    <v-speed-dial
      v-model="optionsOpen"
      direction="top"
      transition="slide-y-reverse-transition"
      class="pdf-fab pdf-fab--options"
    >
      <template v-slot:activator>
        <v-btn v-model="optionsOpen" fab dark color="grey darken-3" elevation="4">
          <v-icon v-if="optionsOpen">mdi-close</v-icon>
          <v-icon v-else>mdi-dots-vertical</v-icon>
        </v-btn>
      </template>

      <v-btn fab dark small color="info" @click="downloadPDF">
        <v-icon>mdi-download-outline</v-icon>
      </v-btn>

      <v-btn fab dark small color="success" @click="sharePDF">
        <v-icon>mdi-share-variant-outline</v-icon>
      </v-btn>
    </v-speed-dial>
  </div>
</template>

<script>
// استخدام legacy build لضمان التوافقية
import * as pdfjsLib from 'pdfjs-dist/legacy/build/pdf';

export default {
	props: ['url'],
	data() {
		return {
			pdf: null,
			totalPages: 0,
			loading: false,
			scale: 2.2,
			optionsOpen: false
		};
	},
	watch: {
		url: {
			immediate: true,
			handler(newUrl) {
				if (newUrl) this.loadPDF();
			}
		}
	},
	computed: {
		backIcon() {
			return this.$vuetify.rtl ? 'mdi-arrow-right' : 'mdi-arrow-left';
		}
	},
	created() {
		// إعداد الـ Worker من CDN
		pdfjsLib.GlobalWorkerOptions.workerSrc = `//cdnjs.cloudflare.com/ajax/libs/pdf.js/${pdfjsLib.version}/pdf.worker.min.js`;
	},
	methods: {
		async getPdfFile() {
			if (!this.url) return null;

			const response = await fetch(this.url);
			const blob = await response.blob();
			return new File([blob], 'invoice.pdf', { type: 'application/pdf' });
		},

		async loadPDF() {
			this.loading = true;
			this.totalPages = 0; // تصفير الصفحات عند تحميل ملف جديد
			
			try {
				// disableFontFace: نرسم الخطوط كمسارات (Path2D) بدل الاعتماد على تشكيل الخطوط بالمتصفح
				// لتفادي تفكك حروف اللغة العربية عند العرض في الـ canvas
				const loadingTask = pdfjsLib.getDocument({
					url: this.url,
					disableFontFace: true
				});
				this.pdf = await loadingTask.promise;
				this.totalPages = this.pdf.numPages;

				// انتظر التحديث في DOM ثم ارسم الصفحات
				this.$nextTick(async () => {
					for (let i = 1; i <= this.totalPages; i++) {
						await this.renderPage(i);
					}
				});
			} catch (err) {
				console.error('خطأ في تحميل الملف:', err);
			} finally {
				this.loading = false;
			}
		},

		async renderPage(num) {
			try {
				const page = await this.pdf.getPage(num);
				const viewport = page.getViewport({ scale: this.scale });
			
				// الوصول للـ canvas بطريقة آمنة في Vue 2
				const canvas = this.$refs['canvas_' + num][0];
				if (!canvas) return;

				const context = canvas.getContext('2d');
				canvas.height = viewport.height;
				canvas.width = viewport.width;

				await page.render({
					canvasContext: context,
					viewport: viewport
				}).promise;
			} catch (err) {
				console.error('خطأ في رسم الصفحة ' + num, err);
			}
		},

		// printPDF() {
		// 	// فتح الملف في تبويب جديد وأمر الطباعة
		// 	const win = window.open(this.url, '_blank');
		// 	win.onload = () => {
		// 		win.print();
		// 	};
		// },

		printPDF() {
			// التحقق إذا كان المستخدم يستخدم موبايل أو تابلت
			const isMobile = /iPhone|iPad|iPod|Android/i.test(navigator.userAgent);

			if (isMobile) {
				// على الموبايل: أفضل وسيلة هي فتح الملف مباشرة في تبويب جديد
				// المتصفح سيفتح الـ PDF بمشغله الخاص الذي يحتوي على زر "طباعة" و "تنزيل" جاهزين
				window.open(this.url, '_blank');
			} else {
				// على اللابتوب: تنفيذ الكود الذي أعجبك (فتح ثم قدح أمر الطباعة)
				const printWindow = window.open(this.url, '_blank');
				
				// محاولة الطباعة بعد التأكد من التحميل
				printWindow.onload = function() {
					printWindow.print();
				};

				// حماية إضافية للابتوب
				setTimeout(() => {
					try {
						printWindow.print();
					} catch (e) {
						console.error('لم نتمكن من قدح الطباعة تلقائياً، قد يكون المتصفح حظرها.');
					}
				}, 2000);
			}
		},

		downloadPDF() {
			const link = document.createElement('a');
			link.href = this.url;
			link.setAttribute('download', 'invoice.pdf');
			document.body.appendChild(link);
			link.click();
			link.remove();
		},

		async sharePDF() {
			if (!navigator.share) {
				alert('ميزة المشاركة غير مدعومة في هذا الجهاز أو المتصفح.');
				return;
			}

			try {
				const file = await this.getPdfFile();

				if (file && navigator.canShare && navigator.canShare({ files: [file] })) {
					await navigator.share({
						title: 'الفاتورة',
						text: 'يرجى الاطلاع على الفاتورة المرفقة',
						files: [file]
					});
					return;
				}

				await navigator.share({
					title: 'الفاتورة',
					text: 'يرجى الاطلاع على الفاتورة المرفقة'
				});
			} catch (err) {
				if (err?.name !== 'AbortError') {
					console.error('فشلت المشاركة:', err);
					alert('تعذر فتح نافذة المشاركة.');
				}
			}
		}
	},

	// printPDF() {
	// 	const printWindow = window.open(this.url, '_blank');
    
	// 	// محاولة الطباعة فور التحميل
	// 	printWindow.onload = function() {
	// 		printWindow.print();
	// 		// اختياري: إغلاق النافذة بعد الطباعة
	// 		// printWindow.onafterprint = function() { printWindow.close(); };
	// 	};

	// 	// كخطة بديلة (Fallback) لبعض المتصفحات التي لا تدعم onload على الـ PDF
	// 	setTimeout(() => {
	// 		printWindow.print();
	// 	}, 2000); // انتظر ثانيتين ثم اطلب الطباعة
	// },

	// 2. مشاركة عبر قائمة تطبيقات النظام
	// async sharePDF() {
	// 	// التحقق من دعم المتصفح لميزة المشاركة (Native Share)
	// 	if (navigator.share) {
	// 		try {
	// 			await navigator.share({
	// 				title: 'فاتورة جديدة',
	// 				text: 'يرجى الاطلاع على الفاتورة المرفقة',
	// 				url: this.url, // الرابط الذي سيتم مشاركته في التطبيقات
	// 			});
	// 			console.log('تمت المشاركة بنجاح');
	// 		} catch (err) {
	// 			console.error('فشلت المشاركة أو تم إلغاؤها:', err);
	// 		}
	// 	} else {
	// 		// إذا كان المتصفح لا يدعم (مثل بعض متصفحات الكمبيوتر)
	// 		this.copyToClipboard();
	// 	}
	// },

	// async sharePDF() {
	// 	if (!navigator.share) {
	// 		this.copyToClipboard();
	// 		return;
	// 	}

	// 	try {
	// 		// 1. تحميل الملف وتحويله إلى Blob
	// 		const response = await fetch(this.url);
	// 		const blob = await response.blob();

	// 		// 2. إنشاء ملف من الـ Blob (هذا ما يجعله يظهر في تطبيقات الموبايل كملف)
	// 		const file = new File([blob], 'invoice.pdf', { type: 'application/pdf' });

	// 		// 3. التحقق هل المتصفح يدعم مشاركة "هذا النوع" من الملفات
	// 		if (navigator.canShare && navigator.canShare({ files: [file] })) {
	// 			await navigator.share({
	// 				files: [file],
	// 				title: 'الفاتورة',
	// 				text: 'إليك ملف الفاتورة'
	// 			});
	// 		} else {
	// 		// إذا لم يدعم مشاركة الملفات، نشارك الرابط كنص
	// 			await navigator.share({
	// 				title: 'الفاتورة',
	// 				url: this.url
	// 			});
	// 		}
	// 	} catch (err) {
	// 		console.error('فشلت المشاركة:', err);
	// 		this.copyToClipboard();
	// 	}
	// },

	// وظيفة مساعدة لنسخ الرابط إذا كانت المشاركة غير مدعومة
	async copyToClipboard() {
		try {
			await navigator.clipboard.writeText(this.url);
			alert('ميزة المشاركة غير مدعومة في هذا المتصفح، تم نسخ الرابط بدلاً من ذلك.');
		} catch (err) {
			alert('رابط الفاتورة: ' + this.url);
		}
	}
};
</script>

<style scoped>
.pdf-main-container {
  position: relative;
  display: flex;
  flex-direction: column;
  width: 100%;
  height: calc(100vh - 130px);
  border: 1px solid #ddd;
}

.pdf-fab {
  position: absolute;
  z-index: 5;
}

.pdf-fab--back {
  top: 12px;
  inset-inline-start: 12px;
}

.pdf-fab--page-count {
  top: 12px;
  inset-inline-end: 12px;
}

.pdf-fab--print {
  bottom: 16px;
  inset-inline-end: 16px;
}

.pdf-fab--options {
  bottom: 16px;
  inset-inline-end: 84px;
}

.pdf-scroll-view {
  flex: 1;
  overflow-y: auto;
  background: #888;
  padding: 20px;
}

.page-wrapper {
  background: white;
  margin: 0 auto 20px auto;
  width: fit-content;
  box-shadow: 0 4px 12px rgba(0,0,0,0.3);
}

canvas {
  display: block;
  max-width: 100%;
}

.status-msg {
  text-align: center;
  color: white;
  margin-top: 20px;
}
</style>